import React from 'react'
import Link from '../../../../Link/index'
import ChevronRight from '../../../../Icon/icons/ChevronRight/index'

export interface PlaylistSeeEntireLinkProps {
  to: string
  videoCount: number
  label: string
  /**
   * When true, render an invisible placeholder that reserves the same
   * vertical space (border + padding + line-box) the real link will
   * occupy after hydration. Used by shell-aware row variants while
   * `useRowHydration` is in flight: the shell already knows there will
   * be items, but the items array hasn't populated yet, so without this
   * the footer would mount on resolution and push everything below it
   * down. Mirrors the `CommunityTabBar` `--placeholder` idiom — see
   * `_CommunityTabBar.scss`.
   *
   * Callers should only set this when the shell promises items
   * (`expectedItemCount > 0`); when the row is genuinely empty, omit
   * the link entirely so the empty-state below it doesn't carry a
   * dangling divider.
   */
  placeholder?: boolean
}

function PlaylistSeeEntireLink ({
  to,
  videoCount,
  label,
  placeholder = false,
}: PlaylistSeeEntireLinkProps) {
  if (placeholder) {
    // Render the real label transparently. A single non-breaking space (the
    // original placeholder content) is one line tall, but the resolved label
    // wraps to two lines inside a narrow row at mobile widths — when the
    // real link replaced the single-space placeholder the row grew by a
    // line-height, producing measurable CLS. Rendering the actual label
    // (transparent + non-interactive) keeps line-count and wrapping behavior
    // identical between placeholder and resolved states.
    return (
      <div
        className="playlist-see-entire-link playlist-see-entire-link--placeholder"
        aria-hidden="true"
      >
        <span className="playlist-see-entire-link__link">
          {label}
          <ChevronRight />
        </span>
      </div>
    )
  }

  if (videoCount <= 0) {
    return null
  }

  return (
    <div className="playlist-see-entire-link">
      <Link
        to={to}
        className="playlist-see-entire-link__link"
      >
        {label}
        <ChevronRight />
      </Link>
    </div>
  )
}

export default PlaylistSeeEntireLink
